/**
 * Scroll Reveal Animations Controller
 * Handles scroll-based animation triggers using IntersectionObserver
 * @version 1.0.0
 */

class ScrollAnimations {
  /**
   * @type {IntersectionObserver | null}
   */
  #observer = null;

  /**
   * @type {Set<Element>}
   */
  #animatedElements = new Set();

  /**
   * Configuration options for the animation system
   */
  #config = {
    threshold: 0.1, // Trigger when 10% of element is visible
    rootMargin: '0px 0px -50px 0px', // Start animation slightly before element enters viewport
  };

  constructor() {
    // Check if scroll animations are enabled via theme settings
    if (!this.#isEnabled()) {
      return;
    }

    // Check for IntersectionObserver support
    if (!('IntersectionObserver' in window)) {
      // Graceful degradation - show all elements immediately
      this.#fallbackShow();
      return;
    }

    this.#init();
  }

  /**
   * Check if scroll animations are enabled in theme settings
   * @returns {boolean}
   */
  #isEnabled() {
    return document.body.dataset.scrollAnimations === 'true';
  }

  /**
   * Initialize the animation system
   */
  #init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.#setup());
    } else {
      this.#setup();
    }
  }

  /**
   * Setup IntersectionObserver and observe elements
   */
  #setup() {
    // Create IntersectionObserver instance
    this.#observer = new IntersectionObserver(
      (entries) => this.#handleIntersection(entries),
      this.#config
    );

    // Find and observe all elements with data-animate attribute
    this.#observeElements();

    // Re-observe elements when new content is dynamically added
    this.#setupMutationObserver();
  }

  /**
   * Find all animatable elements and start observing them
   */
  #observeElements() {
    const elements = document.querySelectorAll('[data-animate]');
    
    elements.forEach((element) => {
      // Skip if already animated
      if (this.#animatedElements.has(element)) {
        return;
      }

      // Skip if in theme editor (elements should show immediately)
      if (document.documentElement.classList.contains('shopify-design-mode')) {
        element.classList.add('animate-in');
        return;
      }

      this.#observer.observe(element);
    });
  }

  /**
   * Handle intersection events
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection(entries) {
    entries.forEach((entry) => {
      // Element has entered the viewport
      if (entry.isIntersecting) {
        const element = entry.target;

        // Add animation trigger class
        element.classList.add('animate-in');

        // Mark as animated
        this.#animatedElements.add(element);

        // Stop observing this element (animation only happens once)
        this.#observer.unobserve(element);
      }
    });
  }

  /**
   * Setup MutationObserver to handle dynamically added content
   * Useful for sections that load via AJAX or theme editor changes
   */
  #setupMutationObserver() {
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          // Re-scan for new animatable elements
          this.#observeElements();
        }
      });
    });

    // Observe the entire document for added nodes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Fallback for browsers without IntersectionObserver support
   * Shows all elements immediately
   */
  #fallbackShow() {
    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((element) => {
      element.classList.add('animate-in');
    });
  }

  /**
   * Public method to manually trigger animation on an element
   * @param {Element} element - The element to animate
   */
  animateElement(element) {
    if (!element || this.#animatedElements.has(element)) {
      return;
    }

    element.classList.add('animate-in');
    this.#animatedElements.add(element);

    if (this.#observer) {
      this.#observer.unobserve(element);
    }
  }

  /**
   * Public method to reset an element's animation
   * @param {Element} element - The element to reset
   */
  resetElement(element) {
    if (!element) {
      return;
    }

    element.classList.remove('animate-in');
    this.#animatedElements.delete(element);

    if (this.#observer) {
      this.#observer.observe(element);
    }
  }

  /**
   * Cleanup and disconnect observer
   */
  destroy() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
    this.#animatedElements.clear();
  }
}

// Initialize the scroll animations system
const scrollAnimations = new ScrollAnimations();

// Export for potential external use
if (typeof window !== 'undefined') {
  window.ScrollAnimations = scrollAnimations;
}
